'use client';

/**
 * HomepageV3 Badge / Tag — now consumes the published se3 design-system
 * package (`@startengine/design-system.se3.badge`). This file is a thin
 * re-export adapter so existing local imports
 * (`components/HomepageV3/components/Badge`) keep working unchanged.
 *
 * The se3 package is a faithful port of the original local component:
 * identical props, identical default `data-cy` values
 * (`editorial-badge` / `editorial-tag`), and identical rendered DOM
 * (`<span>` via MUI `Box component="span"`).
 *
 * The package ships PropTypes-derived `.d.ts` files with loose `any` types,
 * so we re-declare the original (correct) prop interfaces here and apply them
 * to the imported components for full type-safety at call sites.
 */
import type { FC, ReactNode } from 'react';
import { Se3Badge, Se3Tag } from '@startengine/design-system.se3.badge';

export type BadgeVariant = 'teal' | 'gold' | 'wine';

export interface BadgeProps {
  variant: BadgeVariant;
  children: ReactNode;
  /** Render a leading status dot (uses currentColor). */
  dot?: boolean;
  /** Optional leading icon node (rendered before the label). */
  icon?: ReactNode;
  className?: string;
  'data-cy'?: string;
}

export interface TagProps {
  children: ReactNode;
  /** Urgency "alert" style (amber border + text, e.g. "Closing Soon"). */
  alert?: boolean;
  /** Optional leading icon (e.g. clock for urgency). */
  icon?: ReactNode;
  className?: string;
  'data-cy'?: string;
}

export const Badge = Se3Badge as unknown as FC<BadgeProps>;
export const Tag = Se3Tag as unknown as FC<TagProps>;

export default Badge;
